import PropTypes from 'prop-types';

export const INTEGRATION_TYPE = {
  COMMUNICATION: 'communication',
  SUPPORT: 'support',
  CRM: 'crm',
  SURVEY: 'survey',
  SCHEDULING: 'scheduling',
};

export const INTEGRATION_VENDORS = {
  ZENDESK: 'zendesk',
  GMAIL: 'gmail',
  GMAIL_V3: 'gmail-v3',
  HUBSPOT: 'hubspot',
  OUTLOOK: 'outlook',
  GOOGLE_CALENDAR: 'google-calendar',
  OUTLOOK_CALENDAR: 'outlook-calendar',
  SLACK: 'slack',
  INTERCOM: 'intercom',
};

export const VENDOR_NAMES = {
  hubspot: 'HubSpot',
  intercom: 'Intercom',
  slack: 'Slack',
};

export const ENTITY_HEADER_MAP = {
  organization: { id: 'org', name: 'Organizations' },
  account: { id: 'acc', name: 'Accounts' },
  contact: { id: 'contacts', name: 'Contacts' },
};

export const EXTENDED_ENTITY_HEADER_MAP = {
  ticket: { id: 'tickets', name: 'Tickets' },
  organization: { id: 'org', name: 'Organizations' },
  account: { id: 'acc', name: 'Accounts' },
  contact: { id: 'contacts', name: 'Contacts' },
};

export const INTEGRATION_TYPE_PROPTYPE = PropTypes.oneOf(
  Object.values(INTEGRATION_TYPE)
);

export const INTEGRATION_TYPE_LABEL = {
  [INTEGRATION_TYPE.COMMUNICATION]: 'Communication',
  [INTEGRATION_TYPE.SUPPORT]: 'Support',
  [INTEGRATION_TYPE.CRM]: 'CRM',
  [INTEGRATION_TYPE.SURVEY]: 'Survey',
  [INTEGRATION_TYPE.SCHEDULING]: 'Scheduling',
};

export const PROVIDER_DISPLAY_NAME_MAP = {
  gmail: 'Gmail',
};

export const INTEGRATIONS_CATALOG = {
  // Each item has a surrogate key called "id". In case we have multiple integration options with same vendor and type,
  // this id can be used to distinguish between the two integration options.
  copper: {
    id: 1,
    vendor: 'copper',
    title: 'Copper',
    type: INTEGRATION_TYPE.CRM,
    info: 'Import and sync data relating to your organisations, accounts, and contacts from Copper to Velaris.',
    enabled: false,
  },
  freshdesk: {
    id: 2,
    vendor: 'freshdesk',
    title: 'Freshdesk',
    type: INTEGRATION_TYPE.SUPPORT,
    info: 'Connect to Freshdesk to import and sync support tickets and various related data points from Freshdesk.',
    enabled: false,
  },
  hubspot: {
    id: 3,
    vendor: 'hubspot',
    title: 'HubSpot',
    type: INTEGRATION_TYPE.CRM,
    info: 'Import and sync data relating to your organisations, accounts, and contacts from HubSpot to Velaris.',
    enabled: true,
  },
  insightly: {
    id: 4,
    vendor: 'insightly',
    title: 'Insightly',
    type: INTEGRATION_TYPE.CRM,
    info: 'Import and sync data relating to your organisations, accounts, and contacts from Insightly to Velaris.',
    enabled: false,
  },
  intercom: {
    id: 5,
    vendor: 'intercom',
    title: 'Intercom',
    type: INTEGRATION_TYPE.SUPPORT,
    info: 'Import and sync your organisation, account, and contact conversations from Intercom to send personalised Intercom conversations from Velaris.',
    enabled: true,
  },
  pipedrive: {
    id: 6,
    vendor: 'pipedrive',
    title: 'Pipedrive',
    type: INTEGRATION_TYPE.CRM,
    info: 'Import and sync data relating to your organisations, accounts, and contacts from Pipedrive to Velaris.',
    enabled: false,
  },
  outlook: {
    id: 7,
    vendor: 'outlook',
    title: 'Outlook',
    type: INTEGRATION_TYPE.COMMUNICATION,
    info: 'Sync emails you exchange with customers via Outlook into Velaris so you can receive emails and send personalized and automated emails from directly within the platform.',
    enabled: true,
  },
  satismeter: {
    id: 8,
    vendor: 'satismeter',
    title: 'Satismeter',
    type: INTEGRATION_TYPE.SURVEY,
    info: 'Integrate customer feedback data collected from Satismeter, such as NPS into Velaris.',
    enabled: false,
  },
  salesforce: {
    id: 9,
    vendor: 'salesforce',
    title: 'Salesforce',
    type: INTEGRATION_TYPE.CRM,
    info: 'Import and sync data relating to your organisations, accounts, and contacts from Salesforce to Velaris.',
    enabled: false,
  },
  slack: {
    id: 10,
    vendor: 'slack',
    title: 'Slack',
    type: INTEGRATION_TYPE.COMMUNICATION,
    info: 'Connect to send Slack messages directly to users or channels from within Velaris, helping to increase team collaboration and productivity, and make customer data accessible whenever it’s needed.',
    enabled: true,
  },
  zendesk: {
    id: 11,
    vendor: 'zendesk',
    title: 'Zendesk',
    type: INTEGRATION_TYPE.SUPPORT,
    info: 'Import and sync support tickets from Zendesk into Velaris to give your team full visibility to customer support data without having to leave the platform.',
    enabled: true,
  },
  gmail: {
    id: 12,
    vendor: 'gmail',
    title: 'Gmail',
    type: INTEGRATION_TYPE.COMMUNICATION,
    info: 'Sync emails you exchange with customers via Gmail into Velaris so you can receive emails and send personalized and automated emails from directly within the platform.',
    enabled: false,
    visible: false,
  },
  'gmail-v3': {
    id: 13,
    vendor: 'gmail-v3',
    title: 'Gmail',
    type: INTEGRATION_TYPE.COMMUNICATION,
    info: 'Sync emails you exchange with customers via Gmail into Velaris so you can receive emails and send personalized and automated emails from directly within the platform.',
    enabled: true,
  },
  'google-calendar': {
    id: 14,
    vendor: 'google-calendar',
    title: 'Google Calendar',
    type: INTEGRATION_TYPE.SCHEDULING,
    info: 'Sync your meetings and calendar events with Velaris to automatically log them as Scheduled Activities in an Entity’s 360 Profile.',
    enabled: true,
    visible: true,
  },
  'outlook-calendar': {
    id: 15,
    vendor: 'outlook-calendar',
    title: 'Outlook Calendar',
    type: INTEGRATION_TYPE.SCHEDULING,
    info: 'Sync your meetings and calendar events with Velaris to automatically log them as Scheduled Activities in an Entity’s 360 Profile.',
    enabled: true,
    visible: true,
  },
  teams: {
    id: 16,
    vendor: 'teams',
    title: 'Teams',
    type: INTEGRATION_TYPE.COMMUNICATION,
    info: 'Connect to send Teams messages directly to users or channels from within Velaris, helping to increase team collaboration and productivity, and make customer data accessible whenever it’s needed.',
    enabled: true,
    visible: true,
  },
};

export const INTEGRATION_STATUS = {
  CONNECTED: 'CONNECTED',
  PENDING: 'PENDING',
  REVOKED: 'REVOKED',
  IN_SYNC: 'INSYNC',
  FAILED: 'FAILED',
  DISCONNECTED: 'DISCONNECTED',
};

export const INTEGRATION_LEVEL_TYPE = {
  USER: 'user',
  TENANT: 'tenant',
};

export const ZENDESK_MAPPING = {
  ORG_MAPPING: 'organizationMapping',
  USER_MAPPING: 'userMapping',
  ACCOUNT_MAPPING: 'accountMapping',
  CUSTOM_MAPPING: 'custom',
  CUSTOM_USER_INPUT: 'customUserInput',
  CUSTOM_ORG_INPUT: 'customOrgInput',
  CUSTOM_ACCOUNT_INPUT: 'customAccountInput',
};

export const ZENDESK_INTEGRATION_MODAL_PHASE = {
  AUTH_PHASE: 1,
  FIELD_MAPPING_PHASE: 2,
};

export const ZENDESK_TICKET_SUMMARY_DAYS_PERIOD = 30;

export const INTEGRATION_SETTING_TYPE = {
  NOTE_SYNC: 'noteSync',
  CONTACT_CREATE: 'contactCreate',
  PUSH_BACK: 'pushBack',
  DEAL_TRIGGER: 'dealBasedTrigger',
  ENTITY_IN_SYNC: 'entityInSync',
  PARENT_LINK: 'linkParent',
  ACTIVITY_SYNC: 'activitySync',
  PARENT_LINK_CREATE: 'linkParentCreate',
  USER_MAPPING: 'userMapping',
  DEDUPLICATION_UPDATE: 'deduplicationUpdate',
  LINK_ENTITY_OF_CONTACT: 'linkEntityOfContact',
  ADDITIONAL_DEDUPLICATION: 'additionalDeduplication',
};

export const INTEGRATION_SYNC_TYPE = {
  NA: 'na',
  EVENT_DRIVEN: 'eventDriven',
  DATA_DRIVEN: 'dataDriven',
};

export const INTEGRATION_CRITERIA_TYPE = {
  CREATE: 'create',
  UPDATE: 'update',
  NA: 'na',
  ALL: 'all',
};

export const NA = 'na';
